import React, { useMemo, useRef } from "react";
import { avatarSrc } from "@/lib/defaults";
import { localeFor } from "@/lib/dateLocale";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { ChatMessage } from "./ChatMessage";

// Lot 728 — les handlers du parent changent à chaque rendu (fonctions fléchées) ; on passe aux bulles mémoïsées des
// relais STABLES qui appellent toujours la dernière version.
const HANDLER_KEYS = ["onLongPress", "onReply", "onReact", "onReactOpen", "onReactionsOpen", "onSendBig", "onQuickForward", "onRetry", "onToggleSelect", "onOpenMedia", "onQuoteClick"];
const useStableHandlers = (handlers) => {
  const ref = useRef(handlers);
  ref.current = handlers;
  return useMemo(() => Object.fromEntries(HANDLER_KEYS.map((k) => [k, (...args) => ref.current[k]?.(...args)])), []);
};

// Centered WhatsApp-style date separator label.
const dayLabel = (iso, lang, t) => {
  if (!iso) return "";
  const d = new Date(iso);
  const now = new Date();
  const startOf = (x) => new Date(x.getFullYear(), x.getMonth(), x.getDate()).getTime();
  const days = Math.round((startOf(now) - startOf(d)) / 86400000);
  if (days <= 0) return t("chat.today", "Aujourd'hui");
  if (days === 1) return t("chat.yesterday", "Hier");
  if (days < 7) return d.toLocaleDateString(localeFor(lang), { weekday: "long" });
  return d.toLocaleDateString(localeFor(lang), { day: "numeric", month: "long" });
};

// The scrollable message stream: empty state, date separators, bubbles + typing dots.
export const MessageList = ({ messages, user, lang, t, chatTarget, peerTyping, peerLastRead, peerLastDelivered, reactTarget, bottomRef, onLongPress, onReply, onReact, onReactOpen, onReactionsOpen, onSendBig, onQuickForward, onRetry, selectMode, selectedIds, onToggleSelect, onOpenMedia, onQuoteClick }) => {
  const h = useStableHandlers({ onLongPress, onReply, onReact, onReactOpen, onReactionsOpen, onSendBig, onQuickForward, onRetry, onToggleSelect, onOpenMedia, onQuoteClick });
  const myAvatar = avatarSrc(user);
  const peerAvatar = avatarSrc(chatTarget);
  // Last non-deleted outgoing message → the only one that shows the Messenger status.
  let lastMineIdx = -1;
  for (let j = messages.length - 1; j >= 0; j -= 1) {
    if (messages[j].sender_id === user.id && !messages[j].is_deleted) { lastMineIdx = j; break; }
  }
  return (
  <>
    {messages.length === 0 && (
      <div className="text-center pt-10">
        <Avatar className="h-14 w-14 mx-auto">
          <AvatarImage src={avatarSrc(chatTarget)} />
          <AvatarFallback>{chatTarget.name?.[0]}</AvatarFallback>
        </Avatar>
        <div className="font-semibold text-sm mt-2">{chatTarget.name}</div>
        <p className="text-xs mt-1" style={{ color: "var(--yalola-text-muted)" }}>{t("chat.first_message", "Écris ton premier message.")}</p>
      </div>
    )}
    {messages.map((m, i) => {
      const prev = messages[i - 1];
      const showDate = !prev || new Date(prev.created_at).toDateString() !== new Date(m.created_at).toDateString();
      return (
        <React.Fragment key={m.id}>
          {showDate && (
            <div className="flex justify-center my-2" data-testid={`chat-date-${m.id}`}>
              <span className="px-3 py-1 rounded-full text-[11px] font-semibold select-none shadow-sm" style={{ color: "var(--yalola-text-muted)", background: "rgba(255,255,255,0.92)", border: "1px solid var(--yalola-border)", backdropFilter: "blur(4px)" }}>
                {dayLabel(m.created_at, lang, t)}
              </span>
            </div>
          )}
          <ChatMessage
            m={m}
            mine={m.sender_id === user.id}
            user={user}
            lang={lang}
            t={t}
            chatTargetName={chatTarget.name}
            senderAvatar={m.sender_id === user.id ? myAvatar : peerAvatar}
            peerLastRead={peerLastRead}
            peerLastDelivered={peerLastDelivered}
            reactTarget={reactTarget}
            isLastMine={i === lastMineIdx}
            onLongPress={h.onLongPress}
            onReply={h.onReply}
            onReact={h.onReact}
            onReactOpen={h.onReactOpen}
            onReactionsOpen={h.onReactionsOpen}
            onSendBig={h.onSendBig}
            onQuickForward={h.onQuickForward}
            onRetry={h.onRetry}
            selectMode={selectMode}
            selected={selectedIds.has(m.id)}
            onToggleSelect={h.onToggleSelect}
            onOpenMedia={h.onOpenMedia}
            onQuoteClick={h.onQuoteClick}
          />
        </React.Fragment>
      );
    })}
    {peerTyping && (
      <div className="flex justify-start" data-testid="chat-typing-indicator">
        <div className="rounded-2xl px-3 py-2.5 bg-white flex items-center gap-1" style={{ border: "1px solid var(--yalola-border)" }}>
          <span className="chat-typing-dot" /><span className="chat-typing-dot" /><span className="chat-typing-dot" />
        </div>
      </div>
    )}
    <div ref={bottomRef} />
  </>
  );
};
